import { Link } from 'react-router-dom';
import { clsx } from 'clsx';
import { useLanguage } from '../hooks/useLanguage';
import { TranslationKey } from '../i18n/translations';

const PLATFORM_COLORS: Record<string, string> = {
  reddit: 'bg-orange-500/10 text-orange-600',
  google_maps: 'bg-green-500/10 text-green-600',
  instagram: 'bg-pink-500/10 text-pink-600',
  twitter: 'bg-blue-500/10 text-blue-600',
  app_store: 'bg-indigo-500/10 text-indigo-600',
  youtube: 'bg-red-500/10 text-red-600',
  tiktok: 'bg-purple-500/10 text-purple-600',
  other: 'bg-gray-500/10 text-gray-600',
};

const PLATFORM_ABBREVS: Record<string, string> = {
  reddit: 'R',
  google_maps: 'G',
  instagram: 'IG',
  twitter: 'X',
  app_store: 'AS',
  youtube: 'YT',
  tiktok: 'TT',
  other: '*',
};

const PLATFORM_KEYS: Record<string, TranslationKey> = {
  reddit: 'platform_reddit',
  google_maps: 'platform_google_maps',
  instagram: 'platform_instagram',
  twitter: 'platform_twitter',
  app_store: 'platform_app_store',
  youtube: 'platform_youtube',
  tiktok: 'platform_tiktok',
  other: 'platform_other',
};

const ACTION_KEYS: Record<string, TranslationKey> = {
  comment: 'action_comment',
  review: 'action_review',
  follow: 'action_follow',
  like: 'action_like',
  share: 'action_share',
  retweet: 'action_retweet',
  subscribe: 'action_subscribe',
  upvote: 'action_upvote',
  post: 'action_post',
};

interface TaskCardProps {
  task: {
    id: number;
    title: string;
    platform: string;
    action: string;
    reward_usd: number;
    slots_remaining: number;
    slots_total: number;
  };
}

export function TaskCard({ task }: TaskCardProps) {
  const { t } = useLanguage();
  const abbrev = PLATFORM_ABBREVS[task.platform] || '*';
  const colorClass = PLATFORM_COLORS[task.platform] || PLATFORM_COLORS.other;
  const reward = (task.reward_usd / 100).toFixed(2);
  const slotsPercent = (task.slots_remaining / task.slots_total) * 100;

  const platformLabel = PLATFORM_KEYS[task.platform] ? t(PLATFORM_KEYS[task.platform]) : task.platform;
  const actionLabel = ACTION_KEYS[task.action] ? t(ACTION_KEYS[task.action]) : task.action;

  return (
    <Link
      to={`/tasks/${task.id}`}
      className="block bg-tg rounded-xl p-4 task-card"
    >
      <div className="flex items-start gap-3">
        {/* Platform Icon */}
        <div className={clsx('w-12 h-12 rounded-xl flex items-center justify-center text-lg font-bold', colorClass)}>
          {abbrev}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-tg truncate">{task.title}</h3>
          <p className="text-sm text-tg-hint">
            {platformLabel} - {actionLabel}
          </p>
        </div>

        {/* Reward */}
        <div className="text-right rtl:text-left">
          <div className="text-lg font-bold text-brand-primary">${reward}</div>
          <div className="text-xs text-tg-hint">{task.slots_remaining} {t('slots_left')}</div>
        </div>
      </div>

      {/* Slots Progress */}
      <div className="mt-3">
        <div className="h-1.5 bg-tg-secondary rounded-full overflow-hidden">
          <div
            className="h-full bg-brand-primary rounded-full transition-all duration-300"
            style={{ width: `${slotsPercent}%` }}
          />
        </div>
      </div>
    </Link>
  );
}
